'use client'

import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { usePathname } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { X } from 'lucide-react'
import Link from 'next/link'
import {
  COOKIE_CONSENT_VERSION,
  DEFAULT_PREFERENCES,
  applyConsent,
  persistPreferences,
  readConsentRecord,
  readStoredPreferences,
  type CookiePreferences,
} from '@/lib/analytics/consent'
import { captureFirstTouchAttribution } from '@/lib/analytics/attribution'

const PRIVACY_HREF = '/legal/privacy'
const SUMMARY =
  'We use cookies to provide essential functionality, understand how you use Aient, and improve your experience. ' +
  'Essential cookies are required for the service to work. You can customize your preferences for analytics and ' +
  'marketing cookies. Learn more in our'
const CATEGORIES = [
  {
    id: 'essential',
    label: 'Essential Cookies (Required)',
    text:
      'These cookies are necessary for the Services to function. They enable core functionality like ' +
      'authentication, security, session management, and load balancing. They cannot be disabled.',
  },
  {
    id: 'analytics',
    label: 'Analytics Cookies',
    text:
      'These cookies help us understand how you use the Platform, which features you use, and how we can ' +
      'improve your experience. We use Google Analytics.',
  },
  {
    id: 'marketing',
    label: 'Marketing Cookies',
    text:
      'These cookies help us measure the effectiveness of our advertising campaigns. We use the LinkedIn Insight Tag ' +
      'and may also use Meta Pixel and TikTok Pixel through Google Tag Manager to measure conversions and ' +
      'advertising audiences. You can opt out without affecting core functionality.',
  },
] as const
const CHANGE_LATER =
  'You can change these preferences at any time in your account settings or by clearing your browser cookies ' +
  'and revisiting this page.'

/**
 * The public website frame (SiteShell, `.hp`) when the current page renders inside it. The banner is
 * portalled there so it takes the new identity from home.css and sits in the page flow after the footer.
 */
function usePublicShell(): HTMLElement | null {
  const pathname = usePathname()
  const [shell, setShell] = useState<HTMLElement | null>(null)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the shell is DOM the new route commits; read it after commit
    setShell(document.querySelector<HTMLElement>('.hp[data-component="landing-page"]'))
  }, [pathname])
  return shell
}

export function CookieConsentBanner() {
  const publicShell = usePublicShell()
  // Always start hidden to avoid hydration mismatch — localStorage is read in useEffect
  const [isVisible, setIsVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>(readStoredPreferences)

  useEffect(() => {
    // Record first-touch marketing attribution on landing (idempotent after the
    // first store; consent-independent first-party attribution). This runs from
    // the banner because it mounts on every page in the root layout.
    captureFirstTouchAttribution()

    // The beforeInteractive consent-default script (when GTM is enabled) already
    // seeds Consent Mode. We still call applyConsent here so the window bridge
    // and aientConsentChange event fire for non-GTM consumers; gtag receives an
    // idempotent update mirroring the default.
    const record = readConsentRecord()
    let timer: number | undefined
    if (!record) {
      // First visit: apply defaults, show banner after short delay
      applyConsent(DEFAULT_PREFERENCES)
      timer = window.setTimeout(() => setIsVisible(true), 1000)
    } else if (record.version === COOKIE_CONSENT_VERSION) {
      // Valid stored consent — apply (preferences already initialized in useState)
      applyConsent(record.preferences)
    } else {
      // Version mismatch / corrupt — show banner on next frame to re-consent
      timer = window.setTimeout(() => setIsVisible(true), 0)
    }
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [])

  const savePreferences = useCallback((prefs: CookiePreferences) => {
    persistPreferences(prefs)
    applyConsent(prefs)
    setIsVisible(false)
  }, [setIsVisible])

  const acceptAll = useCallback(() => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
    }
    savePreferences(allAccepted)
  }, [savePreferences])

  const rejectAll = useCallback(() => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
    }
    savePreferences(essentialOnly)
  }, [savePreferences])

  const saveCustom = useCallback(() => {
    savePreferences(preferences)
  }, [savePreferences, preferences])

  if (!isVisible) return null

  if (publicShell) {
    return createPortal(
      <section
        className="cc"
        role="region"
        aria-labelledby="cc-title"
        data-component="cookie-consent-banner"
        data-variant="public"
      >
        <div className="cc-in">
          <div className="cc-tx">
            <h2 id="cc-title">Cookie Preferences</h2>{' '}
            {!showDetails ? (
              <p>
                {SUMMARY} <Link href={PRIVACY_HREF}>Privacy Policy</Link>.
              </p>
            ) : (
              <>
                <ul className="cc-cats">
                  {CATEGORIES.map(({ id, label, text }) => (
                    <li key={id}>
                      <input
                        type="checkbox"
                        id={`cc-${id}`}
                        aria-describedby={`cc-${id}-d`}
                        checked={id === 'essential' ? true : preferences[id]}
                        disabled={id === 'essential'}
                        onChange={(e) => setPreferences({ ...preferences, [id]: e.target.checked })}
                      />
                      <label htmlFor={`cc-${id}`}>{label}</label>
                      <p id={`cc-${id}-d`}>{text}</p>
                    </li>
                  ))}
                </ul>
                <p className="cc-note">{CHANGE_LATER}</p>
              </>
            )}
          </div>
          <div className="cc-acts">
            {!showDetails ? (
              <>
                <button key="cookie-customize-btn" type="button" className="b2" onClick={() => setShowDetails(true)} data-testid="cookie-customize-btn">Customize</button>
                <button key="cookie-reject-btn" type="button" className="b2" onClick={rejectAll} data-testid="cookie-reject-btn">Reject All</button>
                <button key="cookie-accept-btn" type="button" className="b1" onClick={acceptAll} data-testid="cookie-accept-btn">Accept All</button>
              </>
            ) : (
              <>
                <button key="cookie-reject-custom-btn" type="button" className="b2" onClick={rejectAll} data-testid="cookie-reject-custom-btn">Reject All</button>
                <button key="cookie-save-btn" type="button" className="b1" onClick={saveCustom} data-testid="cookie-save-btn">Save Preferences</button>
              </>
            )}
          </div>
          <button type="button" className="cc-x" onClick={() => setIsVisible(false)} aria-label="Close cookie banner">
            <svg className="sg" aria-hidden="true" viewBox="0 0 16 16"><path d="M3 3l10 10M13 3L3 13" /></svg>
          </button>
        </div>
      </section>,
      publicShell,
    )
  }

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border"
      data-component="cookie-consent-banner"
    >
      <div className="container mx-auto p-4 md:p-6 max-w-5xl">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1">
            <h2 className="text-lg font-semibold mb-2">🍪 Cookie Preferences</h2>
            {!showDetails ? (
              <p className="text-sm text-muted-foreground">
                {SUMMARY}{' '}
                <Link href={PRIVACY_HREF} className="underline hover:text-foreground">
                  Privacy Policy
                </Link>.
              </p>
            ) : (
              <div className="space-y-4 mt-4">
                <div className="border border-border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="essential" 
                      checked={true} 
                      disabled 
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <label htmlFor="essential" className="font-medium text-sm block mb-1">
                        {CATEGORIES[0].label}
                      </label>
                      <p className="text-xs text-muted-foreground">
                        {CATEGORIES[0].text}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border border-border rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="analytics" 
                      checked={preferences.analytics}
                      onCheckedChange={(checked) => 
                        setPreferences({ ...preferences, analytics: checked as boolean })
                      }
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <label htmlFor="analytics" className="font-medium text-sm block mb-1">
                        {CATEGORIES[1].label}
                      </label>
                      <p className="text-xs text-muted-foreground">
                        {CATEGORIES[1].text}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border border-border rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="marketing" 
                      checked={preferences.marketing}
                      onCheckedChange={(checked) => 
                        setPreferences({ ...preferences, marketing: checked as boolean })
                      }
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <label htmlFor="marketing" className="font-medium text-sm block mb-1">
                        {CATEGORIES[2].label}
                      </label>
                      <p className="text-xs text-muted-foreground">
                        {CATEGORIES[2].text}
                      </p>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground mt-4">
                  {CHANGE_LATER}
                </p>
              </div>
            )}
          </div>
          <button
            onClick={() => setIsVisible(false)}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close cookie banner"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 justify-end">
          {!showDetails ? (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowDetails(true)}
                data-testid="cookie-customize-btn"
              >
                Customize
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={rejectAll}
                data-testid="cookie-reject-btn"
              >
                Reject All
              </Button>
              <Button 
                size="sm" 
                onClick={acceptAll}
                data-testid="cookie-accept-btn"
              >
                Accept All
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={rejectAll}
                data-testid="cookie-reject-custom-btn"
              >
                Reject All
              </Button>
              <Button 
                size="sm" 
                onClick={saveCustom}
                data-testid="cookie-save-btn"
              >
                Save Preferences
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

